import { Injectable } from '@angular/core';
import { FirebaseError } from 'firebase/app';
import { LL, BOOT_LOCALE } from 'i18n/i18n.service';
import { getSupportEmail } from 'environments/environment-helpers';
import { getSharedUrlOrigin } from 'util/backend-origin';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { LoadingController } from 'providers/loading-controller';
import { NavController } from 'providers/nav-controller';
import { getPageNameByPath } from 'app/app-page-helpers';
import { openExternalLink } from 'util/open-external-link';
import { AuthService } from 'services/auth.service';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { ExternalFlowService, SignInTokenError } from 'services/external-flow.service';
import { FirebaseService } from 'services/firebase.service';
import { LoggingService } from 'services/logging.service';
import { SsoService } from 'services/sso.service';
import { AccountHintService } from 'services/account-hint.service';
import { CoachManageDeepLinkService } from 'services/coach-manage-deep-link.service';
import { describeError } from '@cf-shared/error-describe';
import { getMyApp } from 'app/app-interface';
import {
  ClaimTokenExchangeSource,
  ClaimTokenSignInOutcome,
  DeepLinkSignInFlow,
  HANDLED_LAUNCH_URL_KEY,
  PendingAccountSwitch,
  SignInTokenFailureKind,
} from 'services/deep-link-signin';

/**
 * Maps this flow's own three failure names onto the enum the login page reads.
 * The two sets deliberately carry the same strings; this table exists so a
 * future rename on either side is a compile error rather than a silent mismatch.
 */
const SIGN_IN_TOKEN_ERROR_BY_KIND: Record<SignInTokenFailureKind, SignInTokenError> = {
  expired: SignInTokenError.Expired,
  network: SignInTokenError.Network,
  other: SignInTokenError.Other,
};

/**
 * Deep links and sign-in tokens (fourth and final slice of deferred finding
 * df-7b66acbeb341).
 *
 * This class is only the wiring. It supplies the real Firebase account, the
 * real alerts and spinner, the real navigation and the real telemetry. The flow
 * itself — which case a sign-in token falls into, when a spinner may be taken
 * down, what a deep link is asking for — is in `DeepLinkSignInFlow`
 * (services/deep-link-signin.ts), which has no Angular in it so the unit-test
 * runner can load it.
 *
 * Lifted out of `AppComponent` with no behavior change. That component still
 * owns WHEN each of these runs during boot; see design-docs/boot-sequence.md.
 */
@Injectable({ providedIn: 'root' })
export class DeepLinkSignInService {
  private readonly flow: DeepLinkSignInFlow;

  constructor(
    private auth: AuthService,
    private firebase: FirebaseService,
    private cloudFunctions: CloudFunctionsService,
    private externalFlow: ExternalFlowService,
    private alertCtrl: AlertController,
    private loadingCtrl: LoadingController,
    private navCtrl: NavController,
    private logging: LoggingService,
    private sso: SsoService,
    private accountHint: AccountHintService,
    private coachManageDeepLink: CoachManageDeepLinkService,
  ) {
    this.flow = new DeepLinkSignInFlow({
      currentUser: () => this.firebase.fbAuth.currentUser,
      signInWithTokenAsync: (signInToken) => this.auth.signInWithTokenAsync(signInToken),
      sleep: (ms) => new Promise<void>(resolve => { setTimeout(resolve, ms); }),
      proceedAsSignedOutAsync: () => this.auth.stopSuppressingNullAuthAsync(),
      exchangeClaimTokenAsync: (claimToken) =>
        this.cloudFunctions.exchangePurchaseClaimTokenAsync({ claimToken }),
      resendMagicLinkAsync: async (email) => {
        // the result is returned, not discarded: it carries the server's refusal
        // to send when several accounts hold this address (#4505)
        return await this.cloudFunctions.signInByLink({
          email,
          newUser: false,
          baseUrl: getSharedUrlOrigin(),
          uiLanguage: BOOT_LOCALE,
        });
      },
      // The one place Firebase's own error class is needed. It stays here so
      // the flow half can be loaded without Firebase.
      isNetworkAuthError: (err) =>
        err instanceof FirebaseError && err.code === 'auth/network-request-failed',
      setSignInTokenFailure: (kind) =>
        this.externalFlow.setSignInTokenError(SIGN_IN_TOKEN_ERROR_BY_KIND[kind]),
      setPendingAccountSwitch: (data) => this.externalFlow.setPendingAccountSwitch(data),
      confirmAccountSwitchAsync: (currentEmail, newEmail) =>
        this.alertCtrl.showAlertAsync<boolean>({
          title: LL.auth.switchAccount.title(),
          message: LL.auth.switchAccount.message({ currentEmail, newEmail }),
          buttons: [
            { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel, result: false },
            { text: LL.auth.switchAccount.switchButton(), role: AlertButtonRole.Primary, result: true },
          ],
        }),
      showLinkExpiredAlertAsync: async (email) => {
        await this.alertCtrl.showAlertAsync({
          title: LL.auth.switchAccount.expiredTitle(),
          message: LL.auth.switchAccount.expiredMessage({ email }),
          buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: null }],
        });
      },
      showAmbiguousAccountAlertAsync: async () => {
        await this.alertCtrl.showAlertAsync({
          message: LL.auth.errors.otlAmbiguousAccount({ email: getSupportEmail() }),
          buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: null }],
        });
      },
      showLinkResendFailedAlertAsync: async (email) => {
        await this.alertCtrl.showAlertAsync({
          title: LL.auth.switchAccount.expiredTitle(),
          message: LL.auth.switchAccount.expiredResendFailedMessage({ email }),
          buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: null }],
        });
      },
      showAccountSwitchUnfinishedAlertAsync: async (newEmail) => {
        await this.alertCtrl.showAlertAsync({
          title: LL.auth.switchAccount.unfinishedTitle(),
          message: LL.auth.switchAccount.unfinishedMessage({ newEmail }),
          buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: null }],
        });
      },
      presentSigningInLoaderAsync: async () => {
        const loader = this.loadingCtrl.create({ content: LL.common.loading.signingIn() });
        await loader.present();
        return { dismissAsync: () => loader.dismiss() };
      },
      reloadAppAtRoot: () => {
        void getMyApp().reloadAppAsync({ resetToRoot: true });
      },
      resolvePageName: (path) => getPageNameByPath(path),
      navigateToPageAsync: (pageName, params) => this.navCtrl.internalSetRootAsync(pageName, params),
      openExternalLinkAsync: (url) => openExternalLink(url),
      noteAccountHint: (parse) => this.accountHint.noteFromDeepLink(parse),
      // #4688: deliberately NOT openExternalLink — see completeExchangeAsync.
      completeCommunityLoginAsync: (sso, sig) => this.sso.completeExchangeAsync(sso, sig),
      // #6433: both wait for the app to finish starting when the link is what started it
      enterCoachClientAsync: (clientUid, page) => this.coachManageDeepLink.enterClientAccountFromLinkAsync(clientUid, page),
      openClientPageAsync: (page) => this.coachManageDeepLink.openClientPageAsync(page),
      navigateWithinAppTo: (pathAndQuery) => {
        window.location.href = pathAndQuery;
      },
      adoptBootParamsInPlace: (pathAndQuery) => this.externalFlow.adoptBootParamsInPlace(pathAndQuery),
      adoptedBootParamsStillUnread: () => this.externalFlow.hasExternalFlowPending(),
      readHandledLaunchUrl: () => this.readHandledLaunchUrl(),
      markLaunchUrlHandled: (url) => this.markLaunchUrlHandled(url),
      reportProblem: (name, context) => this.logging.trackSilentProblem(name, context),
    });
  }

  /**
   * Read back the cold-start launch link this web-view session already acted on.
   *
   * Session storage survives a reload and dies with the app, which is the exact
   * lifetime this needs. A browser that refuses it answers "nothing was handled",
   * so a real deep link still works and the loop is still ended by the other rule
   * in `decideLaunchUrlHandling`. The refusal is reported rather than swallowed,
   * because it silently weakens this guard. See design-docs/purchase-deep-linking.md, "The launch link is acted on ONCE per web-view session".
   */
  private readHandledLaunchUrl(): string | undefined {
    try {
      return sessionStorage.getItem(HANDLED_LAUNCH_URL_KEY) ?? undefined;
    } catch (err) {
      this.logging.trackSilentProblem('LaunchUrlMarkUnreadable', { message: describeError(err) });
      return undefined;
    }
  }

  /** Remember a cold-start launch link. See `readHandledLaunchUrl` for the storage choice. */
  private markLaunchUrlHandled(url: string): void {
    try {
      sessionStorage.setItem(HANDLED_LAUNCH_URL_KEY, url);
    } catch (err) {
      this.logging.trackSilentProblem('LaunchUrlMarkUnwritable', { message: describeError(err) });
    }
  }

  /**
   * Act on the link that started the app, at most once per web-view session
   * (#4994). The current address is read here rather than passed in, because the
   * only caller is the boot sequence and it always means "wherever we are now".
   */
  handleLaunchUrlOnceAsync(launchUrl: URL): Promise<void> {
    return this.flow.handleLaunchUrlOnceAsync(launchUrl, window.location.pathname + window.location.search);
  }

  /**
   * Hand a cold-start launch link's boot parameters to the boot already running,
   * if that is what the link is for. Answers whether it did. See the flow half.
   */
  adoptLaunchUrlBootParamsAsync(launchUrl: URL): Promise<boolean> {
    return this.flow.adoptLaunchUrlBootParamsAsync(launchUrl, window.location.pathname + window.location.search);
  }

  /** Give up on a dead boot's adopted launch-link parameters. See the flow half. */
  forgetAdoptedBootParams(): void {
    this.flow.forgetAdoptedBootParams();
  }

  /** Handle a sign-in token found during boot. See the flow half for the three cases. */
  processSignInTokenAsync(signInToken: string, claimToken?: string): Promise<void> {
    return this.flow.processSignInTokenAsync(signInToken, claimToken);
  }

  /** Ask about, and carry out, an account switch that boot deferred until the UI existed. */
  handlePendingAccountSwitchAsync(data: PendingAccountSwitch): Promise<void> {
    return this.flow.handlePendingAccountSwitchAsync(data);
  }

  /**
   * Trade a purchase claim token for a sign-in token and use it. Never throws;
   * answers whether it signed in, or is still signing in past its wait limit.
   */
  exchangeClaimTokenAndSignInAsync(claimToken: string, source: ClaimTokenExchangeSource): Promise<ClaimTokenSignInOutcome> {
    return this.flow.exchangeClaimTokenAndSignInAsync(claimToken, source);
  }

  /**
   * The install-referrer exchange for an answer that arrived after the boot
   * stopped waiting for it, so the login page may already be up. Signs in only if
   * nobody has signed in by the time the exchange returns (df-febb6fcfb9e9).
   */
  exchangeLateInstallReferrerClaimTokenAsync(claimToken: string): Promise<ClaimTokenSignInOutcome> {
    return this.flow.exchangeClaimTokenAndSignInAsync(claimToken, 'install-referrer', undefined, true);
  }

  /** Act on a deep link the operating system handed us. */
  handleDeepLinkAsync(url: URL): Promise<void> {
    return this.flow.handleDeepLinkAsync(url);
  }
}
